import React from "react";

import { Formik } from "formik";
import { useSelector } from "react-redux";
import { getValidationSchema } from "components/ConsignmentForm/validationSchema";
import { ConsignmentModalSection } from "constants/navigation";
import { SaleyardPermissions } from "constants/permissions";
import { SaleType, TransactionalSaleTypes } from "constants/sale";

import {
  getBusinessById,
  getCurrentSaleType,
  getCurrentSaleyard,
  getIsLivestockAgent,
} from "selectors";

import { useHasPermission } from "hooks";
import { ConsignmentFormBody } from "./ConsignmentFormBody";

type ConsignmentFormValues = {
  vendor_id: number | null;
  quantity: number | null;
  hasArrived: boolean;
  useVendorAddressAsPickupAddress: boolean;
  receivalLotIds?: number[];
  draftingAttributes?: {
    accreditationGrassFed: boolean | null;
    accreditationAntibioticFree: boolean | null;
    accreditationEU: boolean | null;
    accreditationPCAS: boolean | null;
    accreditationPTIC: boolean | null;
    drenched: boolean | null;
    gudairApproved: boolean | null;
    backline: boolean | null;
    SAEligible: boolean | null;
    sixInOne: boolean | null;
    b12Vac: boolean | null;
    MAndTS: boolean | null;
    painRelief: boolean | null;
  };
  [key: string]: any;
};

interface ConsignmentFormProps {
  associatedCarrierChargesCount?: number;
  carrierName?: string;
  consignmentId?: number | null;
  consignmentsByVendorNumber?: Record<string, any>;
  formRef: React.Ref<any>;
  isCreate: boolean;
  livestockSaleId: number;
  onSubmit: (values: any) => void;
  restrictConsignedFrom?: boolean;
  setIsSubmitEnabled: (isEnabled: boolean) => void;
  showSaleLots: boolean;
  showClearingSection: boolean;
  showNominations: boolean;
  values?: Partial<ConsignmentFormValues>;
  deploymentSaleId?: number;
  selectedSaleType?: SaleType | null;
  requireConsignedFrom?: boolean;
  requiresAgency?: boolean;
  saleyardName?: string;
  section: ConsignmentModalSection;
  changeSection?: (section: string) => void;
  readOnly?: boolean;
}

const ConsignmentForm = ({
  associatedCarrierChargesCount = 0,
  carrierName = "",
  consignmentId = null,
  consignmentsByVendorNumber,
  formRef,
  isCreate,
  livestockSaleId,
  onSubmit,
  requiresAgency = false,
  restrictConsignedFrom,
  setIsSubmitEnabled,
  showSaleLots,
  showClearingSection,
  showNominations,
  values,
  deploymentSaleId,
  selectedSaleType = null,
  requireConsignedFrom = false,
  saleyardName,
  section,
  changeSection = null,
  readOnly,
}: ConsignmentFormProps) => {
  const existingVendor = useSelector(getBusinessById(values.vendor_id));

  let branchId;

  const isLivestockAgent = useSelector(getIsLivestockAgent);

  const hasReceivalLotPermission = useHasPermission(
    getCurrentSaleyard,
    SaleyardPermissions.featureReceivalLots,
  );

  if (isLivestockAgent) {
    branchId = existingVendor && existingVendor.branchId;
  }

  const isSaleYardAuction = selectedSaleType === SaleType.SALEYARD_AUCTION;

  const formDefaults = {
    vendor_id: null,
    quantity: null,
    hasArrived: false,
    useVendorAddressAsPickupAddress: true,
  };

  const initialValues = {
    ...formDefaults,
    ...values,
    branch_id: branchId,
  };

  if (!hasReceivalLotPermission) {
    // Prevent user without permission overwriting any receival lots
    delete initialValues.receivalLotIds;
  }

  // For a consignment, if not set, initialize (relevant) drafting attributes as unset.
  // Note that this could be species driven, but the default is null anyway - this is more about
  // the form rendering a namespaced sub-value.
  if (!initialValues.draftingAttributes) {
    initialValues.draftingAttributes = {
      accreditationGrassFed: null,
      accreditationAntibioticFree: null,
      accreditationEU: null,
      accreditationPCAS: null,
      accreditationPTIC: null,
      drenched: null,
      gudairApproved: null,
      backline: null,
      SAEligible: null,
      sixInOne: null,
      b12Vac: null,
      MAndTS: null,
      painRelief: null,
    };
  }

  const saleType = useSelector(getCurrentSaleType);

  const includeCarrierCharge =
    TransactionalSaleTypes.includes(saleType) && isLivestockAgent;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={getValidationSchema(
        requiresAgency,
        restrictConsignedFrom || requireConsignedFrom,
        isSaleYardAuction,
        includeCarrierCharge,
      )}
      innerRef={formRef}
    >
      <ConsignmentFormBody
        associatedCarrierChargesCount={associatedCarrierChargesCount}
        carrierName={carrierName}
        changeSection={changeSection}
        consignmentId={consignmentId}
        consignmentsByVendorNumber={consignmentsByVendorNumber}
        deploymentSaleId={deploymentSaleId}
        isCreate={isCreate}
        livestockSaleId={livestockSaleId}
        readOnly={readOnly}
        requireConsignedFrom={requireConsignedFrom}
        restrictConsignedFrom={restrictConsignedFrom}
        setIsSubmitEnabled={setIsSubmitEnabled}
        selectedSaleType={selectedSaleType}
        saleyardName={saleyardName}
        section={section}
        showClearingSection={showClearingSection}
        showNominations={showNominations}
        showSaleLots={showSaleLots}
      />
    </Formik>
  );
};

export default ConsignmentForm;
